import { createSlice } from "@reduxjs/toolkit";
import { getSubscriptionAPI, getSubscriptionUsagesAPI, getActiveJobCountAPI } from "./apis/subscription-manager";

/**
 * AI 분석 요금제 및 사용량(DAM-135) 상태.
 *
 * 섹션별로 { pending, error, data, fetchedAt } 를 독립적으로 관리한다.
 * - error 에는 boolean 이 아니라 **HTTP status** 를 담는다.
 *   /subscriptions/me 의 404("활성 구독 없음")를 다른 실패와 구분해야 하기 때문.
 * - fetchedAt 은 "데이터 업데이트 일시" 표기와, 진행 중인 달의 기준 기간 종료 시각에 쓰인다.
 * - prevUsage 를 usage 와 분리한 이유: 월 select 를 바꾸면 usage 가 새 달로 교체되는데,
 *   같은 섹션에 담으면 당월과 전월이 서로 덮어쓰고 두 요청이 병렬이라 race 도 생긴다.
 *   분리해두면 전월 조회 실패가 증감 표시만 숨기고 본 화면은 그대로 유지된다.
 */
const emptySection = { pending: false, error: false, data: null, fetchedAt: null };

const initialState = {
    subscription: { ...emptySection },
    usage: { ...emptySection },
    prevUsage: { ...emptySection },
    activeJobCount: { ...emptySection },
};

// data 는 유지한다(재조회/월 전환 중 깜빡임 방지). 초기 로드는 어차피 null.
const pendingReducer = (key) => (state) => {
    state[key] = { ...state[key], pending: true, error: false };
};

// 조회 시각은 prepare 에서 찍는다. 리듀서 안에서 Date.now() 를 부르면 리듀서가 순수하지 않게 된다.
const successReducer = (key) => ({
    reducer(state, action) {
        state[key] = { pending: false, error: false, data: action.payload, fetchedAt: action.meta.fetchedAt };
    },
    prepare: (data) => ({ payload: data, meta: { fetchedAt: Date.now() } }),
});

const failureReducer = (key) => (state, action) => {
    state[key] = { ...state[key], pending: false, error: action.payload, data: null };
};

const aiBillingSlice = createSlice({
    name: "aiBilling",
    initialState,
    reducers: {
        resetAiBilling: () => initialState,

        subscriptionPending: pendingReducer("subscription"),
        subscriptionSuccess: successReducer("subscription"),
        subscriptionFailure: failureReducer("subscription"),

        usagePending: pendingReducer("usage"),
        usageSuccess: successReducer("usage"),
        usageFailure: failureReducer("usage"),

        prevUsagePending: pendingReducer("prevUsage"),
        prevUsageSuccess: successReducer("prevUsage"),
        prevUsageFailure: failureReducer("prevUsage"),

        activeJobCountPending: pendingReducer("activeJobCount"),
        activeJobCountSuccess: successReducer("activeJobCount"),
        activeJobCountFailure: failureReducer("activeJobCount"),
    },
});

export const aiBillingActions = aiBillingSlice.actions;
export const { resetAiBilling } = aiBillingActions;

/** 네트워크 오류처럼 응답 자체가 없으면 status 를 못 얻으므로 true 로 대체한다. */
const toErrorStatus = (error) => error?.response?.status ?? true;

/**
 * 현재 테넌트의 활성 구독 정보 조회.
 *
 * 404 는 "활성 구독 없음"이라는 **정상 상태**이므로 실패로 처리하지 않고 data:null 로 저장한다.
 * (구독이 없어도 사용료/사용량은 조회 가능하므로 화면 전체를 막아서는 안 된다.)
 */
export const getSubscription = () => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(aiBillingActions.subscriptionPending());
    try {
        const { data } = await getSubscriptionAPI(stage.id, stage.endpoint, project.id);
        dispatch(aiBillingActions.subscriptionSuccess(data));
        return data;
    } catch (error) {
        if (error?.response?.status === 404) {
            dispatch(aiBillingActions.subscriptionSuccess(null));
            return null;
        }
        dispatch(aiBillingActions.subscriptionFailure(toErrorStatus(error)));
        throw error;
    }
};

/**
 * 선택한 달의 사용량/사용료 조회.
 * @param {string} month - "YYYY-MM"
 * @param {string} [offset] - "+09:00"
 */
export const getUsage = (month, offset) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(aiBillingActions.usagePending());
    try {
        const { data } = await getSubscriptionUsagesAPI(stage.id, stage.endpoint, project.id, { month, offset });
        dispatch(aiBillingActions.usageSuccess(data));
        return data;
    } catch (error) {
        dispatch(aiBillingActions.usageFailure(toErrorStatus(error)));
        throw error;
    }
};

/**
 * 전월 사용량 조회(전월 대비 증감 표시 전용).
 *
 * 다른 thunk 와 달리 **에러를 재전파하지 않는다.** 증감은 부가 정보이므로
 * 전월 조회 실패가 본 화면 렌더를 막아서는 안 된다. 실패 시 error 만 기록하고 조용히 끝낸다.
 * @param {string} month - 전월 키 "YYYY-MM"
 * @param {string} [offset] - "+09:00"
 */
export const getPrevUsage = (month, offset) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(aiBillingActions.prevUsagePending());
    try {
        const { data } = await getSubscriptionUsagesAPI(stage.id, stage.endpoint, project.id, { month, offset });
        dispatch(aiBillingActions.prevUsageSuccess(data));
        return data;
    } catch (error) {
        dispatch(aiBillingActions.prevUsageFailure(toErrorStatus(error)));
        return null;
    }
};

/** 진행 중인 AI Job 수와 한도 조회. */
export const getActiveJobCount = () => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(aiBillingActions.activeJobCountPending());
    try {
        const { data } = await getActiveJobCountAPI(stage.id, stage.endpoint, project.id);
        dispatch(aiBillingActions.activeJobCountSuccess(data));
        return data;
    } catch (error) {
        dispatch(aiBillingActions.activeJobCountFailure(toErrorStatus(error)));
        throw error;
    }
};

export default aiBillingSlice.reducer;
